import { useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase.js';

// Super-admin tool — grant a manual (no-Stripe) subscription, e.g. for a customer
// who paid by bank transfer. Sets planTier + manualExpiresAt; a daily scheduled
// function downgrades back to Free when the date passes.

export default function SuperBilling() {
  const [vendorId, setVendorId] = useState('');
  const [tier, setTier] = useState('enterprise');
  const [aiAddon, setAiAddon] = useState(false);
  // Default expiry: 1 year from today.
  const oneYearFromNow = new Date();
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
  const [expires, setExpires] = useState(oneYearFromNow.toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null); setResult(null);
    try {
      const fn = httpsCallable(functions, 'grantManualSubscription');
      // Treat the date input as end-of-day in the operator's local timezone so
      // a "2026-12-31" grant remains active through that whole day.
      const expDate = new Date(`${expires}T23:59:59`);
      const res = await fn({
        vendorId: vendorId.trim(),
        tier,
        aiAddon,
        expiresAt: expDate.toISOString(),
      });
      setResult(res.data);
    } catch (err) {
      setError(err.message || 'Could not grant subscription.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={page}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0 }}>Super-admin · Manual subscription</h2>
        <Link to="/" style={{ fontSize: 13 }}>← Home</Link>
      </div>
      <p style={{ color: 'var(--muted)' }}>
        Use this for vendors who pay out-of-band (bank transfer, invoice, etc.). It sets
        their plan directly — no Stripe charge. They drop back to Free automatically when
        the expiry date passes.
      </p>

      <form onSubmit={submit} style={box}>
        <label style={lbl}>Vendor ID</label>
        <input
          value={vendorId} onChange={(e) => setVendorId(e.target.value)}
          required placeholder="vendor doc id"
          style={input}
        />

        <label style={lbl}>Plan</label>
        <select value={tier} onChange={(e) => setTier(e.target.value)} style={input}>
          <option value="basic">Basic</option>
          <option value="enterprise">Enterprise</option>
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 14 }}>
          <input type="checkbox" checked={aiAddon} onChange={(e) => setAiAddon(e.target.checked)} />
          AI add-on enabled
        </label>

        <label style={lbl}>Expires (end of day, local time)</label>
        <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} required style={input} />

        <button type="submit" disabled={busy || !vendorId.trim()} className="btn-primary" style={{ marginTop: 16 }}>
          {busy ? 'Granting…' : 'Grant subscription'}
        </button>
      </form>

      {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}
      {result && (
        <div style={{ ...box, background: '#ecfdf5', borderColor: '#a7f3d0', color: '#065f46' }}>
          <strong>Granted ✓</strong>
          <div style={{ fontSize: 13, marginTop: 4 }}>
            Vendor <code>{result.vendorId}</code> · Plan <strong>{result.tier}</strong>
            {result.aiAddon ? ' + AI add-on' : ''} · Expires {new Date(result.expiresAt).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
}

const page = { maxWidth: 640, margin: '40px auto', padding: '0 16px' };
const box = {
  border: '1px solid var(--border, #e2e8f0)',
  borderRadius: 'var(--radius, 10px)',
  padding: 18, marginTop: 12, background: 'var(--card, #fff)',
};
const lbl = { display: 'block', marginTop: 12, marginBottom: 4, fontSize: 13, fontWeight: 600 };
const input = { width: '100%', maxWidth: 360, padding: '8px 10px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 8, fontSize: 14 };
